/**
 * Decomp: GoldSrc-BSP (Version 30) -> Brushes (.map, Valve 220)
 *
 * Eigenständig (nur Raumbaum-Leser, Körpergeometrie und Texte), läuft im Browser und in Node; die Kommandozeile steht in scripts/decomp.mjs.
 *   const r = Decomp.decompile(arrayBuffer, opts);   // r.entities, r.stats, r.warnings
 *   const text = Decomp.toMapText(r);                // .map-Text (Valve 220)
 *
 * Verfahren: Der Raumbaum (Hull 0) wird von der Wurzel abwärts durchlaufen. Jeder Knoten teilt den
 * aktuellen konvexen Körper an seiner Ebene. Ein Blatt mit festem Inhalt (solid, Himmel, Wasser ...)
 * ergibt einen Brush. Ist ein ganzer Teilbaum einheitlich fest, wird er zu einem einzigen Brush
 * zusammengefasst. Texturen kommen von den BSP-Flächen, die auf den Brush-Seiten liegen.
 *
 * Grenzen: Brushes sind Bruchstücke der Originalbrushes (der Raumbaum hat sie zerschnitten). Clip-Hüllen,
 * Patches und die ursprüngliche Brush-Aufteilung sind nicht rekonstruierbar. Seiten ohne passende
 * sichtbare Fläche bekommen die Ersatztextur (opts.nullTex).
 */
import type { Vec3 } from '../model/types.ts';
import I18n from '../i18n.ts';
import { add, cross, dot, len, sub, boxBody, clipBody, bodyVolume, clipPoly, polyArea, unit } from '../model/body.ts';
import type { Body, Side } from '../model/body.ts';
import { CONTENTS, parseEntities, readBspTree } from './bsp-tree.ts';
import type { BspTree } from './bsp-tree.ts';

/** Dekompilier-Optionen (Standardwerte siehe decompile) */
export interface DecompOptions { margin: number; nullTex: string; collapse: boolean; minVolume: number; fluids: boolean; originBrushes: boolean; entText?: string }
/** Fläche eines rekonstruierten Brushes: drei Ebenenpunkte, Textur und Achsen s/t als [x, y, z, Versatz] */
export interface DecompFace { pts: [Vec3, Vec3, Vec3]; tex: string; s: number[]; t: number[]; rot: number; xs: number; ys: number; matched: boolean }
export interface DecompBrush { faces: DecompFace[] }
export interface DecompEntity { kv: Record<string, string>; order: string[]; brushes: DecompBrush[] }
export interface DecompStats { brushes: number; faces: number; texturedFaces: number; dropped: number; models: number; entities: number }
export interface DecompResult { entities: DecompEntity[]; stats: DecompStats; warnings: string[] }
interface FaceHit { n: Vec3; d: number; pts: Vec3[]; info: number }

const Decomp = (function () {
  'use strict';
  const _ = I18n.t;
  const readBsp = readBspTree;

  function faceVerts(b: BspTree, f: { first: number; num: number }): Vec3[] {
    const v: Vec3[] = [];
    for (let i = 0; i < f.num; i++) {
      const se = b.surf[f.first + i], e = b.edges[Math.abs(se)];
      v.push(b.verts[se >= 0 ? e[0] : e[1]]);
    }
    return v;
  }

  /** Flächen des Modells nach (gerundeter) Normale gruppieren, damit passende Seiten schnell gefunden werden. */
  function buildFaceIndex(b: BspTree, first: number, num: number): { find(n: Vec3, d: number): FaceHit[] } {
    const bins = new Map<string, FaceHit[]>(), key = (x: number, y: number, z: number): string => x + ',' + y + ',' + z;
    for (let i = first; i < first + num; i++) {
      const f = b.faces[i], pl = b.planes[f.plane]; if (!pl || f.num < 3) continue;
      const n: Vec3 = f.side ? [-pl.n[0], -pl.n[1], -pl.n[2]] : pl.n, d = f.side ? -pl.d : pl.d;
      const pts = faceVerts(b, f);
      const k = key(Math.round(n[0] * 100), Math.round(n[1] * 100), Math.round(n[2] * 100));
      let bin = bins.get(k); if (!bin) { bin = []; bins.set(k, bin); }
      bin.push({ n: n, d: d, pts: pts, info: f.info });
    }
    return {
      find(n: Vec3, d: number): FaceHit[] {
        const out: FaceHit[] = [], cx = Math.round(n[0] * 100), cy = Math.round(n[1] * 100), cz = Math.round(n[2] * 100);
        for (let x = -1; x <= 1; x++) for (let y = -1; y <= 1; y++) for (let z = -1; z <= 1; z++) {
          const l = bins.get(key(cx + x, cy + y, cz + z)); if (!l) continue;
          l.forEach((f) => { if (dot(f.n, n) > 0.9995 && Math.abs(f.d - d) < 0.1) out.push(f); });
        }
        return out;
      }
    };
  }

  /** Überdeckung zweier koplanarer konvexer Polygone (Fläche der Schnittmenge). */
  function overlapArea(face: Vec3[], sidePts: Vec3[], n: Vec3): number {
    let poly = face, pn: Vec3 = [0, 0, 0], sg = 1;
    for (let i = 1; i < sidePts.length - 1; i++) pn = add(pn, cross(sub(sidePts[i], sidePts[0]), sub(sidePts[i + 1], sidePts[0])));
    if (dot(pn, n) < 0) sg = -1; // Umlaufsinn der Seite
    for (let i = 0; i < sidePts.length && poly.length >= 3; i++) {
      const a = sidePts[i], b = sidePts[(i + 1) % sidePts.length];
      const en = unit(cross(sub(b, a), n)).map((x) => x * sg) as Vec3; // zeigt aus dem Polygon heraus (n nach außen, Umlauf gegen den Uhrzeigersinn)
      poly = clipPoly(poly, en, dot(en, a));
    }
    return poly.length >= 3 ? polyArea(poly) : 0;
  }

  // ---------------------------------------------------------------- Brush-Ausgabe
  function threePoints(pts: Vec3[], n: Vec3): [Vec3, Vec3, Vec3] {
    let best: [Vec3, Vec3, Vec3] | null = null, ba = -1;
    const m = pts.length;
    for (let i = 0; i < m - 2; i++) for (let j = i + 1; j < m - 1; j++) for (let k = j + 1; k < m; k++) {
      const ar = len(cross(sub(pts[j], pts[i]), sub(pts[k], pts[i])));
      if (ar > ba) { ba = ar; best = [pts[i], pts[j], pts[k]]; }
    }
    let [a, bb, c] = best as [Vec3, Vec3, Vec3];
    if (dot(cross(sub(a, bb), sub(c, bb)), n) < 0) { const t = a; a = c; c = t; }
    return [a, bb, c];
  }

  function makeBrush(b: BspTree, body: Body, index: { find(n: Vec3, d: number): FaceHit[] }, opts: DecompOptions, shift: Vec3, fluidTex?: string): { faces: DecompFace[]; textured: number } {
    const faces: { s: Side; ti: BspTree['texinfo'][number] | null; name: string | null }[] = [];
    let fallbackFluid: string | null = null, textured = 0;
    body.forEach((s) => {
      let best = null as FaceHit | null, ba = 0.5;
      index.find(s.n, s.d).forEach((c) => { const a = overlapArea(c.pts, s.pts, s.n); if (a > ba) { ba = a; best = c; } });
      const ti = best ? b.texinfo[best.info] : null;
      const name = ti && b.miptex[ti.tex] ? b.miptex[ti.tex] : null;
      if (name) textured++;
      if (name && (name[0] === '!' || name[0] === '*') && !fallbackFluid) fallbackFluid = name;
      faces.push({ s: s, ti: ti, name: name });
    });
    const fluid = fallbackFluid || fluidTex;
    const out: DecompFace[] = [];
    faces.forEach((f) => {
      const p = threePoints(f.s.pts, f.s.n).map((q) => add(q, shift)) as [Vec3, Vec3, Vec3];
      const name = f.name || (fluidTex !== undefined ? fluid : null) || opts.nullTex;
      let sx = [1, 0, 0, 0], tx = [0, 1, 0, 0], xs = 1, ys = 1;
      if (f.ti) {
        const ls = len(f.ti.s), lt = len(f.ti.t);
        const su: Vec3 = ls > 1e-9 ? (f.ti.s.map((x) => x / ls) as Vec3) : [1, 0, 0], tu: Vec3 = lt > 1e-9 ? (f.ti.t.map((x) => x / lt) as Vec3) : [0, 1, 0];
        xs = ls > 1e-9 ? 1 / ls : 1; ys = lt > 1e-9 ? 1 / lt : 1;
        // Verschiebung des Brushes: u = s·p + so, mit p' = p + shift gilt so' = so - s·shift
        sx = [su[0], su[1], su[2], f.ti.so - dot(f.ti.s, shift)]; tx = [tu[0], tu[1], tu[2], f.ti.to - dot(f.ti.t, shift)];
      } else {
        // Seite ohne passende BSP-Fläche: Achsen passend zur Seitenlage wählen
        const an = [Math.abs(f.s.n[0]), Math.abs(f.s.n[1]), Math.abs(f.s.n[2])];
        if (an[2] >= an[0] && an[2] >= an[1]) { sx = [1, 0, 0, 0]; tx = [0, -1, 0, 0]; } else if (an[0] >= an[1]) { sx = [0, 1, 0, 0]; tx = [0, 0, -1, 0]; } else { sx = [1, 0, 0, 0]; tx = [0, 0, -1, 0]; }
      }
      out.push({ pts: p, tex: name, s: sx, t: tx, rot: 0, xs: xs, ys: ys, matched: !!f.ti });
    });
    return { faces: out, textured: textured };
  }

  // ---------------------------------------------------------------- Raumbaum durchlaufen
  function decompileModel(b: BspTree, model: BspTree['models'][number], opts: DecompOptions, stats: DecompStats, shift: Vec3, isWorld: boolean): DecompBrush[] {
    const brushes: DecompBrush[] = [];
    const mg = isWorld ? opts.margin : 0.5;
    const root = boxBody(sub(model.min, [mg, mg, mg]), add(model.max, [mg, mg, mg]));
    const index = buildFaceIndex(b, model.first, model.num);
    const wanted = (c: number): boolean => c === CONTENTS.SOLID || c === CONTENTS.SKY || (opts.fluids && c <= CONTENTS.WATER && c >= -5) || (opts.fluids && c <= -9 && c >= -14);
    const uni = new Map<number, number | null>(); // Knoten -> einheitlicher Inhalt oder null
    const uniform = (ch: number): number | null => {
      if (ch < 0) return b.leafs[~ch] ? b.leafs[~ch].contents : CONTENTS.SOLID;
      if (uni.has(ch)) return uni.get(ch) as number | null;
      const nd = b.nodes[ch]; uni.set(ch, null);
      const a = uniform(nd.ch[0]), c = uniform(nd.ch[1]);
      const r = a !== null && a === c ? a : null; uni.set(ch, r); return r;
    };
    const emit = (body: Body | null, contents: number): void => {
      if (!body || bodyVolume(body) < opts.minVolume) { stats.dropped++; return; }
      const fluid = contents <= CONTENTS.WATER && contents !== CONTENTS.SKY && contents !== CONTENTS.ORIGIN && contents !== CONTENTS.CLIP;
      const br = makeBrush(b, body, index, opts, shift, fluid ? (b.miptex.find((n) => n && (n[0] === '!' || n[0] === '*')) || opts.nullTex) : undefined);
      stats.brushes++; stats.faces += br.faces.length; stats.texturedFaces += br.textured;
      brushes.push({ faces: br.faces });
    };
    const rec = (ch: number, body: Body | null, depth: number): void => {
      if (!body) return;
      if (depth > 400) { stats.dropped++; return; }
      const c = uniform(ch);
      if (c !== null && ch >= 0 && opts.collapse) { if (wanted(c)) emit(body, c); return; }
      if (ch < 0) { if (wanted(c as number)) emit(body, c as number); return; }
      const nd = b.nodes[ch], pl = b.planes[nd.plane];
      // children[0] = vor der Ebene (n·p > d), children[1] = dahinter
      rec(nd.ch[1], clipBody(body, pl.n, pl.d), depth + 1);
      rec(nd.ch[0], clipBody(body, [-pl.n[0], -pl.n[1], -pl.n[2]], -pl.d), depth + 1);
    };
    // Einheitliche Wurzel (z. B. Brushmodelle aus einem Block) wird durch den Zweig oben behandelt
    if (model.head < 0) { if (wanted(b.leafs[~model.head].contents)) emit(root, b.leafs[~model.head].contents); } else rec(model.head, root, 0);
    return brushes;
  }

  function originCube(c: Vec3): DecompBrush {
    const h = 8, mk = (n: Vec3, d: number, pts: Vec3[]): Side => ({ n: n, d: d, pts: pts });
    const [x, y, z] = c, x0 = x - h, x1 = x + h, y0 = y - h, y1 = y + h, z0 = z - h, z1 = z + h;
    const body = [
      mk([1, 0, 0], x1, [[x1, y0, z0], [x1, y1, z0], [x1, y1, z1], [x1, y0, z1]]), mk([-1, 0, 0], -x0, [[x0, y1, z0], [x0, y0, z0], [x0, y0, z1], [x0, y1, z1]]),
      mk([0, 1, 0], y1, [[x1, y1, z0], [x0, y1, z0], [x0, y1, z1], [x1, y1, z1]]), mk([0, -1, 0], -y0, [[x0, y0, z0], [x1, y0, z0], [x1, y0, z1], [x0, y0, z1]]),
      mk([0, 0, 1], z1, [[x0, y0, z1], [x1, y0, z1], [x1, y1, z1], [x0, y1, z1]]), mk([0, 0, -1], -z0, [[x0, y1, z0], [x1, y1, z0], [x1, y0, z0], [x0, y0, z0]])
    ];
    return { faces: body.map((s) => ({ pts: threePoints(s.pts, s.n), tex: 'ORIGIN', s: [1, 0, 0, 0], t: [0, -1, 0, 0], rot: 0, xs: 1, ys: 1, matched: false })) };
  }

  // ---------------------------------------------------------------- Hauptfunktion
  function decompile(buf: ArrayBuffer, options?: Partial<DecompOptions>): DecompResult {
    const opts: DecompOptions = Object.assign({ margin: 32, nullTex: 'NULL', collapse: true, minVolume: 1, fluids: true, originBrushes: true }, options || {});
    const b = readBsp(buf), stats: DecompStats = { brushes: 0, faces: 0, texturedFaces: 0, dropped: 0, models: b.models.length, entities: 0 }, warnings: string[] = [];
    const ents = parseEntities(opts.entText || b.entText), out: DecompEntity[] = [];
    ents.forEach((e, i) => {
      const kv = Object.assign({}, e.kv), order = e.order.slice();
      let mi = -1;
      if (i === 0) mi = 0; else if (/^\*\d+$/.test(kv.model || '')) mi = parseInt(kv.model.slice(1), 10);
      const ent: DecompEntity = { kv: kv, order: order, brushes: [] };
      if (mi >= 0 && mi < b.models.length) {
        let shift: Vec3 = [0, 0, 0];
        const hasOrigin = i > 0 && kv.origin && opts.originBrushes;
        if (hasOrigin) { const q = kv.origin.trim().split(/\s+/).map(Number); shift = [q[0], q[1], q[2]]; }
        if (shift.some((x) => !isFinite(x))) shift = [0, 0, 0];
        ent.brushes = decompileModel(b, b.models[mi], opts, stats, shift, i === 0);
        if (i > 0) { delete kv.model; ent.order = order.filter((k) => k !== 'model'); }
        if (hasOrigin) { ent.brushes.push(originCube(shift)); delete kv.origin; ent.order = ent.order.filter((k) => k !== 'origin'); }
        if (i > 0 && !ent.brushes.length) warnings.push(_('decomp.w_nobrushes', { cls: kv.classname || '?', model: kv.model }));
      }
      out.push(ent);
    });
    stats.entities = out.length;
    if (!out.length || out[0].kv.classname !== 'worldspawn') warnings.push('Kein worldspawn als erste Entity');
    if (stats.dropped) warnings.push(_('decomp.w_dropped', { count: stats.dropped }));
    const missing = stats.faces - stats.texturedFaces;
    if (missing > 0) warnings.push(_('decomp.w_missing', { missing: missing, total: stats.faces, tex: opts.nullTex }));
    return { entities: out, stats: stats, warnings: warnings };
  }

  // ---------------------------------------------------------------- Text
  const fmt = (x: number): string => { const r = Math.round(x * 10000) / 10000; return String(Object.is(r, -0) ? 0 : r); };
  const pt = (v: number[]): string => '( ' + fmt(v[0]) + ' ' + fmt(v[1]) + ' ' + fmt(v[2]) + ' )';
  function faceLine(f: DecompFace): string {
    return pt(f.pts[0]) + ' ' + pt(f.pts[1]) + ' ' + pt(f.pts[2]) + ' ' + f.tex +
      ' [ ' + fmt(f.s[0]) + ' ' + fmt(f.s[1]) + ' ' + fmt(f.s[2]) + ' ' + fmt(f.s[3]) + ' ] [ ' + fmt(f.t[0]) + ' ' + fmt(f.t[1]) + ' ' + fmt(f.t[2]) + ' ' + fmt(f.t[3]) + ' ] ' +
      fmt(f.rot) + ' ' + fmt(f.xs) + ' ' + fmt(f.ys);
  }
  function toMapText(res: DecompResult): string {
    const o: string[] = [];
    res.entities.forEach((e, i) => {
      o.push('{');
      const kv = Object.assign({}, e.kv);
      if (i === 0) kv.mapversion = '220';
      const keys = ['classname'].concat((e.order || Object.keys(kv)).filter((k) => k !== 'classname' && k in kv));
      if (i === 0 && keys.indexOf('mapversion') < 0) keys.push('mapversion');
      keys.forEach((k) => { if (kv[k] !== undefined) o.push('"' + k + '" "' + String(kv[k]).replace(/"/g, '') + '"'); });
      e.brushes.forEach((br) => { o.push('{'); br.faces.forEach((f) => o.push(faceLine(f))); o.push('}'); });
      o.push('}');
    });
    return o.join('\n') + '\n';
  }

  /** Inhalt (Punktprobe im Raumbaum): für Tests und eigene Prüfungen. Gibt den Leaf-Inhalt am Punkt zurück. */
  function pointContents(bspBuf: ArrayBuffer | BspTree, model: number, p: Vec3): number {
    const b = 'nodes' in bspBuf ? bspBuf : readBsp(bspBuf); let n = b.models[model].head;
    while (n >= 0) { const nd = b.nodes[n], pl = b.planes[nd.plane]; n = nd.ch[dot(pl.n, p) - pl.d >= 0 ? 0 : 1]; }
    return b.leafs[~n].contents;
  }

  return { decompile, toMapText, readBsp, pointContents, CONTENTS };
})();
export default Decomp;
